import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {
  DeleteContainerContentsQuery,
  DeleteContainerContentsQueryVariables,
} from 'samples/containers/__generated/DeleteContainerContentsQuery';
import * as StorableActions from 'samples/StorableActions';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {apiIdToIconName} from 'util/APIUtil';
import sortedBy from 'util/sortedBy';

type ContainerContent = ExtractByTypename<
  DeleteContainerContentsQuery['containers'][number],
  'ContainerNode'
>['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export interface DeleteContainerContentsModalProps {
  containerIds: Array<string>;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
  onSuccess?: () => void;
}

interface BioentityOrBatch {
  id: string;
  name: string;
  tagSchema?: {
    id: string;
    iconId?: string;
  } | null;
}

interface State {
  selectedContainedItems: Set<BioentityOrBatch> | null;
  canProceedOnSampleStatus: boolean;
  selectedSampleIds: Array<string>;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  areWarningsLoading: boolean;
}

interface QueryingProps {
  containersWithTransfers: Array<{
    id: string;
    name: string;
    hasOutputTransfers: boolean;
    hasInputTransfers: boolean;
  }>;
  containedItemsByContainerId: {[containerId: string]: Array<BioentityOrBatch>};
}

export const DELETE_CONTAINER_CONTENTS_QUERY = gql`
  query DeleteContainerContentsQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        hasOutputTransfers
        hasInputTransfers
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
              name
              tagSchema {
                id
                iconId
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function coerceInaccessibleContainedItem(containedItem: ContainedItem): BioentityOrBatch {
  if (!isInaccessible(containedItem)) {
    return containedItem;
  }
  return {id: containedItem.invalidId, name: '(No access)'};
}

function QueryingDeleteContainerContentsModal(props: DeleteContainerContentsModalProps): JSX.Element | null {
  const result = useBenchlingQuery<DeleteContainerContentsQuery, DeleteContainerContentsQueryVariables>(
    DELETE_CONTAINER_CONTENTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
      variables: {containerIds: props.containerIds},
    }
  );
  switch (result.type) {
    case QueryResultType.PENDING: {
      return (
        <Modal onRequestClose={props.onRequestClose} title="Remove contents from containers">
          <CenteredHorizontalThrobber />
        </Modal>
      );
    }
    case QueryResultType.SUCCESS: {
      const containers = accessibleObjs(result.data.containers);
      // TODO(BNCH-94075): You should also not be able to submit in the case of inaccessible contents, rather
      // than the backend catching it
      const containedItemsByContainerId = objectFromEntries(
        containers.map((container) => [
          container.id,
          getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(container.contents).map(
            ({containedItem}) => coerceInaccessibleContainedItem(containedItem)
          ),
        ])
      );
      const containersWithTransfers = containers.filter(
        (container) => container.hasOutputTransfers || container.hasInputTransfers
      );
      return (
        <DeleteContainerContentsModal
          {...props}
          containersWithTransfers={containersWithTransfers}
          containedItemsByContainerId={containedItemsByContainerId}
        />
      );
    }
    default:
      return null;
  }
}

// Only exported for tests
export class DeleteContainerContentsModal extends React.Component<
  DeleteContainerContentsModalProps & QueryingProps,
  State
> {
  state: State = {
    selectedContainedItems: null,
    canProceedOnSampleStatus: false,
    selectedSampleIds: [],
    shouldShowStatusWarnings: false,
    samples: null,
    areWarningsLoading: true,
  };

  async checkForWarnings(): Promise<void> {
    const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
      sampleIds: this.props.containerIds,
    });
    this.setState({shouldShowStatusWarnings: shouldShowWarning, samples: samples ?? null});
    this.setSelectedSampleIds(this.props.containerIds);
    this.setState({areWarningsLoading: false});
  }

  setCanProceedOnSampleStatus = (override: boolean): void => {
    this.setState({canProceedOnSampleStatus: override});
  };

  setSelectedSampleIds = (sampleIds: Array<string>): void => {
    this.setState({selectedSampleIds: sampleIds});
    const selectedContainedItems = new Set(
      sampleIds
        .flatMap((sampleId) => this.props.containedItemsByContainerId[sampleId])
        .filter((content) => content != null)
    );
    this.setState({selectedContainedItems});
  };

  componentDidMount(): void {
    this.checkForWarnings();
  }

  private handleSubmit = (): void => {
    Context.usingAsync({analyticsMetadata: {actionOrigin: this.props.actionOrigin}}, null, async () => {
      await StorableActions.deleteContainerContentsWithDataMods(
        this.state.selectedSampleIds,
        this.props.onSuccess
      );
    });
    this.props.onRequestClose();
  };

  statusWarningTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionAttempted: 'remove_contents',
    actionOrigin: this.props.actionOrigin,
    eventGroupId: Analytics.makeAnalyticsEventId(),
  };

  getDeletionWarning = (): string => {
    const filteredContainersWithOutputTransfers = this.props.containersWithTransfers.filter(
      (container) => container.hasOutputTransfers && this.state.selectedSampleIds.includes(container.id)
    );
    const filteredContainersWithInputTransfers = this.props.containersWithTransfers.filter(
      (container) => container.hasInputTransfers && this.state.selectedSampleIds.includes(container.id)
    );
    const containerLabel = this.state.selectedSampleIds.length > 1 ? 'these containers' : 'this container';
    const transferDirections = [
      ...(filteredContainersWithInputTransfers.length > 0 ? ['from'] : []),
      ...(filteredContainersWithOutputTransfers.length > 0 ? ['to'] : []),
    ];
    const transferText =
      transferDirections.length > 0
        ? `The transfer history for ${containerLabel} will still appear on ${containerLabel} and any other containers ${
            this.state.selectedSampleIds.length > 1 ? `they've` : `it's`
          } been transferred ${transferDirections.join(' or ')}.`
        : '';
    const transferWarning = `${transferText} You'll also still be able to view ${
      this.state.selectedSampleIds.length > 1 ? 'their transfer histories' : 'its transfer history'
    } in the audit log of each container. `;
    const auditText = `This action cannot be undone.`;
    return `${transferDirections.length > 0 ? transferWarning : ''}${auditText}`;
  };

  render(): JSX.Element {
    const sortedContainedItems = sortedBy([...(this.state.selectedContainedItems ?? [])], ['name']);
    const containedItemTable = (
      <div>
        <Row>
          <Col md={12}>
            {sortedContainedItems.length === 0 ? (
              <p className={css(styles.warningFontSize)}>The selected containers are empty.</p>
            ) : (
              <div>
                <p className={css(styles.warningFontSize)} id="deletion-warning">
                  {this.getDeletionWarning()}
                </p>
                <Table className="simpleTable--short" numRows={sortedContainedItems.length}>
                  <Column
                    header="Contents"
                    renderCell={(rowIndex) => (
                      <Cell>
                        <BenchlingIcon
                          className="faded u-small-spacing-right"
                          icon={
                            (sortedContainedItems[rowIndex].tagSchema?.iconId as BenchlingIconName) ||
                            apiIdToIconName(sortedContainedItems[rowIndex].id)
                          }
                        />
                        {sortedContainedItems[rowIndex].name}
                      </Cell>
                    )}
                  />
                </Table>
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
    const possibleWarning = this.state.shouldShowStatusWarnings && this.state.samples != null && (
      <SampleStatusWarning
        sampleIds={this.props.containerIds}
        selectedSampleIds={this.state.selectedSampleIds}
        setSelectedSampleIds={this.setSelectedSampleIds}
        shouldProceed={this.state.canProceedOnSampleStatus}
        setShouldProceed={this.setCanProceedOnSampleStatus}
        trackingParams={this.statusWarningTrackingParams}
        samples={this.state.samples}
      />
    );
    return (
      <Modal
        buttons={[
          {
            text: 'Remove contents',
            style: ButtonStyle.NEGATIVE,
            submit: true,
            disabled:
              this.state.selectedContainedItems == null ||
              this.state.selectedContainedItems.size === 0 ||
              !canProceedWithSamples(
                this.state.selectedSampleIds,
                this.state.shouldShowStatusWarnings,
                this.state.canProceedOnSampleStatus
              ),
          },
        ]}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleSubmit}
        title="Remove contents from containers"
      >
        {!this.state.areWarningsLoading ? (
          <>
            {possibleWarning}
            {canProceedWithSamples(
              this.state.selectedSampleIds,
              this.state.shouldShowStatusWarnings,
              this.state.canProceedOnSampleStatus
            ) && containedItemTable}
          </>
        ) : (
          <CenteredHorizontalThrobber />
        )}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  warningFontSize: {fontSize: '13px'},
});

export default QueryingDeleteContainerContentsModal;
